import { useMemo, useState } from "react";

import SemverService from "@services/semverService/semverService";

import { ReleaseFilesState } from "./useReleaseFiles";

const enrollmentReleaseChannels = new Set<API.ReleaseFile["channel"]>(["beta", "live"]);

interface SelectOption {
    label: string;
    value: string;
}

interface UseProductVersionSelectionOptions {
    autoSelect?: boolean;
    releaseFiles: ReleaseFilesState;
    initialCodename?: string | null;
    initialVersion?: string | null;
}

interface UseProductVersionSelectionReturn {
    releaseFiles: ReleaseFilesState;
    selectedProductCodename: string;
    setSelectedProductCodename: (codename: string) => void;
    selectedVersion: string;
    setSelectedVersion: (version: string) => void;
    productOptions: SelectOption[];
    versionOptions: SelectOption[];
    selectedFile: API.ReleaseFile | undefined;
}

export const findEnrollmentReleaseFile = (files: API.ReleaseFile[], codename: string, version: string) =>
    files.find((file) => enrollmentReleaseChannels.has(file.channel) && file.codename === codename && file.version === version);

export const useEnrollmentProductSelection = (options: UseProductVersionSelectionOptions): UseProductVersionSelectionReturn => {
    const { autoSelect = true, releaseFiles, initialCodename, initialVersion } = options;
    const [chosenProductCodename, setChosenProductCodename] = useState<string>(initialCodename ?? "");
    const [chosenVersion, setChosenVersion] = useState<{ codename: string; version: string } | null>(
        initialCodename && initialVersion ? { codename: initialCodename, version: initialVersion } : null,
    );

    const enrollmentReleaseFiles = useMemo(
        () => releaseFiles.data.filter((file) => enrollmentReleaseChannels.has(file.channel)),
        [releaseFiles.data],
    );

    const productOptions = useMemo(() => {
        const uniqueCodenames = [...new Set(enrollmentReleaseFiles.map((file) => file.codename))];
        return uniqueCodenames.map((codename) => {
            const firstFile = enrollmentReleaseFiles.find((file) => file.codename === codename);
            return {
                label: firstFile?.productName || codename,
                value: codename,
            };
        });
    }, [enrollmentReleaseFiles]);

    const isChosenProductOffered = productOptions.some((option) => option.value === chosenProductCodename);
    const selectedProductCodename = isChosenProductOffered
        ? chosenProductCodename
        : autoSelect
          ? (productOptions[0]?.value ?? "")
          : "";

    const versionOptions = useMemo(() => {
        if (!selectedProductCodename) {
            return [];
        }
        const versions = [
            ...new Set(
                enrollmentReleaseFiles.filter((file) => file.codename === selectedProductCodename).map((file) => file.version),
            ),
        ];
        return versions
            .sort((a, b) => SemverService.compare(b, a))
            .map((version) => ({
                label: version,
                value: version,
            }));
    }, [enrollmentReleaseFiles, selectedProductCodename]);

    const isChosenVersionOffered =
        chosenVersion?.codename === selectedProductCodename &&
        versionOptions.some((option) => option.value === chosenVersion.version);
    const selectedVersion = isChosenVersionOffered ? chosenVersion.version : (versionOptions[0]?.value ?? "");

    const selectedFile = useMemo(() => {
        if (!selectedProductCodename || !selectedVersion) {
            return undefined;
        }
        return findEnrollmentReleaseFile(enrollmentReleaseFiles, selectedProductCodename, selectedVersion);
    }, [enrollmentReleaseFiles, selectedProductCodename, selectedVersion]);

    return {
        releaseFiles,
        selectedProductCodename,
        setSelectedProductCodename: setChosenProductCodename,
        selectedVersion,
        setSelectedVersion: (version: string) => setChosenVersion({ codename: selectedProductCodename, version }),
        productOptions,
        versionOptions,
        selectedFile,
    };
};
